import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { dictionaries } from "./dictionaries";
import { pageMeta } from "./paths";
import type { Locale } from "./paths";
import type { Dictionary } from "./types";

interface I18nValue {
  locale: Locale;
  direction: "rtl" | "ltr";
  copy: Dictionary;
  isThankYou: boolean;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  locale,
  isThankYou = false,
  children,
}: {
  locale: Locale;
  isThankYou?: boolean;
  children: ReactNode;
}) {
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      direction: locale === "ar" ? "rtl" : "ltr",
      copy: dictionaries[locale],
      isThankYou,
    }),
    [locale, isThankYou],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = value.direction;
    document.title = pageMeta[isThankYou ? "thankYou" : "landing"][locale].title;
  }, [locale, value.direction, isThankYou]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
